import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Video, MapPin, User, 
  CheckCircle, XCircle, AlertCircle, Plus
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const MySessions = ({ bookId, user }) => {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, [user, bookId]);

  const loadSessions = async () => {
    try {
      // Mock data - replace with actual service call
      const mockSessions = [
        {
          id: '1',
          title: 'React Advanced Patterns - 1:1 Session',
          instructor: 'John Smith',
          instructorAvatar: null,
          date: new Date(Date.now() + 86400000), // Tomorrow
          duration: 60,
          type: 'video',
          status: 'confirmed',
          meetingLink: 'https://meet.example.com/abc123'
        },
        {
          id: '2',
          title: 'Career Coaching Session',
          instructor: 'Jane Doe',
          instructorAvatar: null,
          date: new Date(Date.now() + 172800000), // 2 days
          duration: 30,
          type: 'video',
          status: 'pending',
          meetingLink: null
        }
      ];

      setSessions(mockSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSessions = () => {
    const now = new Date();
    
    switch (filter) {
      case 'upcoming':
        return sessions.filter(s => new Date(s.date) > now && s.status === 'confirmed');
      case 'pending':
        return sessions.filter(s => s.status === 'pending');
      case 'past':
        return sessions.filter(s => new Date(s.date) <= now);
      default:
        return sessions;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { color: 'green', icon: CheckCircle, label: 'Confirmed' },
      pending: { color: 'yellow', icon: AlertCircle, label: 'Pending' },
      cancelled: { color: 'red', icon: XCircle, label: 'Cancelled' },
      completed: { color: 'blue', icon: CheckCircle, label: 'Completed' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-${badge.color}-100 text-${badge.color}-700`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const tabs = [
    { id: 'upcoming', label: 'Upcoming', count: sessions.filter(s => new Date(s.date) > new Date() && s.status === 'confirmed').length },
    { id: 'pending', label: 'Pending', count: sessions.filter(s => s.status === 'pending').length },
    { id: 'past', label: 'Past', count: sessions.filter(s => new Date(s.date) <= new Date()).length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Sessions</h1>
          <p className="text-gray-600">Manage your coaching and mentoring sessions</p>
        </div>
        <Link
          to="/discover"
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Book Session</span>
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                filter === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Sessions List */}
      {getFilteredSessions().length > 0 ? (
        <div className="space-y-6">
          {getFilteredSessions().map((session) => (
            <div key={session.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{session.title}</h3>
                  
                  {/* Instructor */}
                  <div className="flex items-center gap-3 mb-4">
                    {session.instructorAvatar ? (
                      <img 
                        src={session.instructorAvatar}
                        alt={session.instructor}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{session.instructor}</div>
                      <div className="text-sm text-gray-500">Instructor</div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(session.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{formatTime(session.date)} ({session.duration} min)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      <span>Video Call</span>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div>
                  {getStatusBadge(session.status)}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                {session.status === 'confirmed' && session.meetingLink && (
                  <a
                    href={session.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    <Video className="w-4 h-4" />
                    <span>Join Meeting</span>
                  </a>
                )}
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Reschedule
                </button>
                <button className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No {filter} sessions
          </h3>
          <p className="text-gray-600 mb-6">
            {filter === 'upcoming' && 'Book your first coaching session'}
            {filter === 'pending' && 'No sessions awaiting confirmation'}
            {filter === 'past' && 'No past sessions yet'}
          </p>
          <Link
            to="/discover"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Browse Sessions
          </Link>
        </div>
      )}
    </div>
  );
};